import type { MouseEventHandler, KeyboardEventHandler, ChangeEventHandler } from 'react';
import { useRef, useState, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StickyZoneFontSize, StickyZoneShape, StickyZoneText, StickyZoneTitlePosition } from '../../domain';
import { TextAlign } from '@view-model/models/common/text-align';
import { ForeignObjectAutosize2 } from '@view-model/models/common/components/ForeignObjectAutosize';
import { useD3DblClickCallback, useD3MouseDownCallback, useD3MouseUpCallback } from '@view-model/models/common/hooks';
import { classNames } from '@framework/utils';
import { flushSync } from 'react-dom';
import type { Size } from '@view-model/models/common/basic';

const textAlignClass: Record<TextAlign, string> = {
  [TextAlign.Left]: 'text-left',
  [TextAlign.Center]: 'text-center',
  [TextAlign.Right]: 'text-right',
};

const textDisplayTextAlignClass: Record<TextAlign, string> = {
  [TextAlign.Left]: 'justify-start',
  [TextAlign.Center]: 'justify-center',
  [TextAlign.Right]: 'justify-end',
};

type Props = {
  size: { width: number; height: number };
  isMyEditing: boolean; // 自分が編集中かどうか(RTDB側の値)
  otherUserEditing: boolean;
  textSelectable: boolean;
  text: StickyZoneText;
  fontSize: StickyZoneFontSize;
  textAlign: TextAlign;
  shape: StickyZoneShape;
  titlePosition: StickyZoneTitlePosition | null;
  isHyperLink: boolean;
  onEditStart(): void;
  onChange(text: StickyZoneText): void;
  onEditEnd(text: StickyZoneText): void;
};

type State = {
  isEditing: boolean;
  editingText: string;
};

export const StickyZoneTextarea: React.FC<Props> = ({
  size,
  isMyEditing,
  otherUserEditing,
  textSelectable,
  text,
  fontSize,
  textAlign,
  shape,
  titlePosition,
  isHyperLink,
  onChange,
  onEditStart,
  onEditEnd,
}: Props) => {
  const { t } = useTranslation();
  const textRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { width, height } = size;

  // Left/Right は横幅 1/3 のコンテナで上下中央に配置（横書きのまま）
  const isLeftRight = titlePosition === StickyZoneTitlePosition.Left || titlePosition === StickyZoneTitlePosition.Right;
  const containerWidth = isLeftRight ? Math.floor(width / 3) : width;

  // paddingClass で使う左右マージン（片側 px）
  const MX_DEFAULT = 24;
  const MX_LEFT_RIGHT = 16;
  const horizontalMarginPx = isLeftRight ? MX_LEFT_RIGHT : MX_DEFAULT;

  // 円形(楕円)のとき、テキスト開始位置(mt-10 = 40px)での弦の長さをテキスト幅として使用
  // heightが変わっても正確に追従するよう、実際のテキスト開始y座標から弦の幅を計算
  // Left/Right の場合はコンテナが 1/3 幅の矩形なので弦計算は行わない
  const isCircle = shape === StickyZoneShape.Circle;
  const textWidth = (() => {
    if (isLeftRight) {
      return `max(0px, calc(100% - ${horizontalMarginPx * 2}px))`;
    }
    if (!isCircle) return `calc(100% - ${horizontalMarginPx * 2}px)`;
    const halfHeight = height / 2;
    if (halfHeight <= 0) {
      return `${width}px`;
    }
    const yFromCenter = halfHeight - 40;
    // 中心からの距離を 0..1 の範囲に正規化して clamp し、常に sqrt の中身が 0..1 になるようにする
    const normalizedDistance = Math.min(1, Math.max(0, Math.abs(yFromCenter) / halfHeight));
    const chordWidth = width * Math.sqrt(1 - normalizedDistance * normalizedDistance);
    return `${chordWidth}px`;
  })();

  // Bottom / Left / Right 配置のためにレンダリング済みブロック高さを追跡する
  const [renderedBlockHeight, setRenderedBlockHeight] = useState(0);
  const handleSizeChange = (newSize: Size) => setRenderedBlockHeight(newSize.height);

  // ForeignObjectAutosize2 の transform で <foreignObject> 要素自体を移動することで
  // クリック判定領域とコンテンツ表示位置を一致させる
  // Left:   x=0, 上下中央
  // Right:  x=(width - 1/3), 上下中央
  // Bottom: x=0, y=(height - renderedBlockHeight)
  const foreignObjectTransform = (() => {
    const centerY = (height - renderedBlockHeight) / 2;
    switch (titlePosition) {
      case StickyZoneTitlePosition.Bottom:
        return `translate(0, ${height - renderedBlockHeight})`;
      case StickyZoneTitlePosition.Left:
        return `translate(0, ${centerY})`;
      case StickyZoneTitlePosition.Right:
        return `translate(${width - containerWidth}, ${centerY})`;
      default:
        return undefined;
    }
  })();

  // titlePosition に応じたパディングクラスを計算する
  // Bottom のみ上下マージンを反転、Left/Right は上下対称マージン
  const paddingClass = (() => {
    if (titlePosition === StickyZoneTitlePosition.Bottom) {
      return isCircle ? 'mt-6 mb-10' : 'mx-6 mt-6 mb-10';
    }
    if (isLeftRight) {
      return 'mx-4 my-4';
    }
    return isCircle ? 'mb-6 mt-10' : 'mx-6 mb-6 mt-10';
  })();

  const [state, setState] = useState<State>({
    isEditing: false,
    editingText: '',
  });

  const startEdit = (selection?: { start: number; end: number }) => {
    onEditStart();

    flushSync(() => {
      setState((prev) => (prev.isEditing ? prev : { ...prev, isEditing: true, editingText: text.value }));
    });
    const textarea = textareaRef.current;

    if (!textarea) {
      console.warn('Textarea is not rendered yet. This should not happen.');
      return;
    }

    textarea.focus();

    if (selection) {
      textarea.setSelectionRange(selection.start, selection.end);
    } else {
      const length = textarea.value.length;
      textarea.setSelectionRange(length, length);
    }
  };

  // テキスト要素のダブルクリックイベントを紐付ける
  useD3DblClickCallback(textRef, () => startEdit(), true);

  // 通常時(非編集時)のテキスト領域のクリック、ドラッグイベントを紐付ける
  // ゾーンのテキスト表示時にドラッグでテキスト選択できるように、(ゾーン自体が移動しないように)イベント伝搬を止める
  useD3MouseDownCallback(textRef, () => void 0, true);
  useD3MouseUpCallback(
    textRef,
    async () => {
      if (!textSelectable) return;
      // ユーザがテキスト上のクリック or ドラッグで選択していれば、その範囲をstartEditに渡す
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        startEdit({
          start: range.startOffset,
          end: range.endOffset,
        });
      } else {
        startEdit();
      }
    },
    true
  );

  const handleKeyDown: KeyboardEventHandler = (event) => {
    const withMetaKey = event.ctrlKey || event.metaKey;
    if (event.key == 'Enter' && withMetaKey && textareaRef.current) {
      textareaRef.current.blur();
    }
  };

  const handleMouseDownCapture: MouseEventHandler = (event) => {
    // テキスト選択できるようにイベント伝搬を止める
    event.stopPropagation();
  };

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
    const value = event.target.value;
    const newText = new StickyZoneText(value);
    setState((prev) => ({ ...prev, editingText: value }));
    onChange(newText);
  };

  const handleBlur = () => {
    const newText = new StickyZoneText(state.editingText);
    setState((prev) => ({ ...prev, isEditing: false }));
    onEditEnd(newText);
  };

  const textValue = state.isEditing ? state.editingText : text.value;

  useLayoutEffect(() => {
    // 高さ調整
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  });

  return (
    <ForeignObjectAutosize2 onSizeChange={handleSizeChange} transform={foreignObjectTransform} hideOnOverview>
      <div style={{ width: containerWidth, fontSize: StickyZoneFontSize.getFontSize(fontSize) }}>
        <div
          className="size-full items-start justify-center"
          style={{
            display: !state.isEditing ? 'none' : 'flex',
          }}
        >
          <textarea
            ref={textareaRef}
            className={classNames(
              paddingClass,
              'resize-none border-none bg-transparent align-middle font-bold outline-none',
              textAlignClass[textAlign]
            )}
            value={textValue}
            placeholder={t('canvas.zone.textarea.placeholder')}
            rows={1}
            onKeyDown={handleKeyDown}
            onMouseDownCapture={handleMouseDownCapture}
            onChange={handleChange}
            onBlur={handleBlur}
            readOnly={!isMyEditing}
            style={{ width: textWidth, ...(isCircle ? { marginLeft: 'auto', marginRight: 'auto' } : {}) }}
          />
        </div>
        <div
          ref={textRef}
          className={classNames(
            paddingClass,
            'items-start whitespace-break-spaces break-all font-bold',
            textDisplayTextAlignClass[textAlign],
            textAlignClass[textAlign],
            isHyperLink ? 'text-brand underline' : 'text-black no-underline'
          )}
          style={{
            width: textWidth,
            display: state.isEditing ? 'none' : 'flex',
            cursor: textSelectable ? (!otherUserEditing ? 'text' : 'not-allowed') : 'default',
            ...(isCircle ? { marginLeft: 'auto', marginRight: 'auto' } : {}),
          }}
        >
          {textSelectable ? text.value || t('canvas.zone.textarea.placeholder') : text.value}
        </div>
      </div>
    </ForeignObjectAutosize2>
  );
};
